import { Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import useSWR from 'swr';

import MeetingDetailItem from './meetings/meetingDetailItem';

import { customScrollbarStyle } from '../../../assets/styles/custom.styles';
import { ROLE_VALUES } from '../../../constants/customerConstant';
import {
  MeetingDetailItemObject,
  WISTLEBLOWER,
} from '../../../constants/meetings';
import { MeetingDetailsObjectFromAPI } from '../../../interfaces/meetingDetailsObjectFromAPI';
import { setCount, setItems, setLoading } from '../../../slices/meetings';
import { RootState } from '../../../utils/store';
import LoadingComponent from '../../common/loadingComponent';
import PaginationComponent from '../../partner/paginationComponent';

// FILTER THE DATA FROM API TO LIST ONLY THE ACCEPTED MEETINGS BY THE WB USER
const filterDataFromAPI = (
  data: MeetingDetailsObjectFromAPI[],
  isWB?: boolean,
): MeetingDetailsObjectFromAPI[] => {
  if (isWB) {
    return (
      data?.filter((meeting) => {
        let accepted = false;
        if (meeting?.attendees) {
          accepted = meeting.attendees.some(({ attendee, confirm }) => {
            return confirm === 1 && attendee?.user_type === 1;
          });
        }
        return accepted;
      }) ?? []
    );
  }
  return data;
};

const getOrganizerName = (
  item: MeetingDetailsObjectFromAPI,
  isWB?: boolean,
  isAdmin?: boolean,
): string => {
  if (item?.created_by?.user_type === WISTLEBLOWER && isWB) return 'You';
  else if (item?.created_by?.user_type === WISTLEBLOWER)
    return `${item?.created_by?.full_name ?? 'Anonymous'} Reporter`;
  else if (item?.created_by?.user_type !== WISTLEBLOWER && !isAdmin)
    return item?.created_by?.full_name ?? 'You';
  else return item?.created_by?.full_name ?? 'No Name';
};

const getAttendeeName = (
  item: MeetingDetailsObjectFromAPI,
  isWB?: boolean,
  isAdmin?: boolean,
): string => {
  if (isWB) return item?.case_handler?.full_name ?? 'TBA';
  else if (item?.created_by?.user_type !== WISTLEBLOWER && !isAdmin)
    return (
      item?.attendees?.find(({ attendee }) => attendee?.user_type == 1)
        ?.attendee?.full_name ?? 'Wistleblower'
    );
  else if (isAdmin && item?.created_by?.user_type === WISTLEBLOWER)
    return item?.case_handler?.full_name ?? 'Unassigned';
  else return item?.case_handler?.full_name ?? 'Unassigned';
};

const transformData = (
  data: MeetingDetailsObjectFromAPI[],
  isWB?: boolean,
  isAdmin?: boolean,
): MeetingDetailItemObject[] => {
  return filterDataFromAPI(data, isWB)?.map(
    (item: MeetingDetailsObjectFromAPI) => ({
      id: item?.id,
      link: item?.link,
      type: item?.type,
      date: item?.date_and_time,
      reportType: item?.report?.report_type,
      organizer: getOrganizerName(item, isWB, isAdmin),
      country: item?.report?.country_verbose?.name ?? '',
      name: getAttendeeName(item, isWB, isAdmin),
      address: item?.address,
    }),
  );
};

interface MeetingListProps {
  isWB?: boolean;
}

const MeetingList = ({ isWB }: MeetingListProps) => {
  const userType = localStorage.getItem('user_type');
  const isAdmin =
    userType === `${ROLE_VALUES.TENANT_ADMIN}` ||
    userType === `${ROLE_VALUES.PARTNER_ADMIN}`;
  const dispatch = useDispatch();
  const meetings = useSelector((state: RootState) => state.meetings.items);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const { data, isLoading, mutate } = useSWR(
    currentPage ? `schedule/meetings?page=${currentPage}` : '',
    { revalidateOnFocus: false },
  );

  useEffect(() => {
    dispatch(setLoading(isLoading));
    if (data) {
      dispatch(setItems(transformData(data.results, isWB, isAdmin)));
      dispatch(setCount(data.count));
    }
  }, [data, isLoading, dispatch, isWB, isAdmin]);

  const setCurrPage = (newPage: number) => {
    setCurrentPage(newPage);
  };

  return (
    <>
      <Stack
        gap={2}
        sx={{
          ...customScrollbarStyle,
          maxHeight: 'auto',
          paddingRight: '20px',
        }}
      >
        {isLoading && <LoadingComponent />}
        {meetings.length > 0 &&
          meetings.map((item) => (
            <MeetingDetailItem
              key={item.id}
              isWB={isWB}
              isAdmin={isAdmin}
              canAddAttendee={true}
              refresh={mutate}
              {...item}
            />
          ))}
        {meetings.length === 0 && (
          <Typography variant='body1' sx={{ alignSelf: 'center' }}>
            <FormattedMessage defaultMessage='No Meetings found.' />
          </Typography>
        )}
      </Stack>
      <Stack>
        <PaginationComponent
          ItemPerPage={25}
          page={currentPage}
          totalPages={Math.ceil(data?.count / 25)}
          totalCount={data?.count}
          changePage={setCurrPage}
          noPaddingY
        />
      </Stack>
    </>
  );
};

export default MeetingList;
